import React from 'react'
import { useTranslation } from 'react-i18next'
import IconS24Checkmark24 from '@is24/cosma-ui-icons/build/tsx/IconS24Checkmark24'
import IconS24LockClose24 from '@is24/cosma-ui-icons/build/tsx/IconS24LockClose24'
import { IconS24ArrowRight16 } from '@is24/cosma-ui-icons'
import { Button } from 'is24-corecss'
import styles from './SegmentationBoxInfo.module.css'
import { SegmentationMetadataConfig } from './SegmentationBoxInfo.config'
import { RealEstateCategory, SegmentationType, TransactionType } from '../../../@types/types'
import { trackSegmentationEvent } from './segmentationTracking'

type SegmentationPointProps = {
  text1?: string
  text2: string
  noBullet?: boolean
}

const SegmentationPoint = ({ text1, text2, noBullet }: SegmentationPointProps) => {
  if (noBullet) {
    return (
      <div className='grid-item one-whole'>
        <span className={`font-body-medium-regular`}>{text2}</span>
      </div>
    )
  }

  return (
    <div className='grid grid-flex gap-s grid-align-start' style={{ flexWrap: 'nowrap' }}>
      <IconS24Checkmark24 className={'grid-item'} style={{ flexShrink: 0, color: 'var(--color-icon-default)' }} />
      <span className={`grid-item`}>
        {text1 ? (
          <>
            <span className={`font-body-medium-bold`}>{text1} </span>
            <span className={`font-body-medium-regular`}>{text2}</span>
          </>
        ) : (
          <span className={`font-body-medium-regular`}>{text2}</span>
        )}
      </span>
    </div>
  )
}

type SecurityNoteProps = {
  text: string
}

const SecurityNote = ({ text }: SecurityNoteProps) => {
  return (
    <div
      className={`grid grid-flex grid-align-center gap-s grid-justify-center font-body-small-regular ${styles.securityNote}`}
    >
      <IconS24LockClose24 className={'grid-item'} />
      <div className={'grid-item'}>{text}</div>
    </div>
  )
}

type SecurityLinkProps = {
  href: string
  label: string
  onClick?: () => void
}

const SecurityLink = ({ href, label, onClick }: SecurityLinkProps) => {
  const handleClick = () => {
    if (onClick) {
      onClick()
    }
    window.open(href, '_blank', 'noopener,noreferrer')
  }

  return (
    <Button className={`display-flex align-items-center gap-s ${styles.securityButton}`} onClick={handleClick}>
      {label} <IconS24ArrowRight16 />
    </Button>
  )
}

export type SegmentationBoxBodyProps = {
  pointsTitle?: string
  pointsTitleClassName?: string
  points: SegmentationMetadataConfig['points']
  securityNoteKey?: string
  securityLink?: SegmentationMetadataConfig['securityLink']
  selectedSegmentation?: SegmentationType | null
  transactionType?: TransactionType | 'SHORT_TERM_ACCOMMODATION' | null
  realEstateCategory?: RealEstateCategory | 'BUSINESS' | null
}

export const SegmentationBoxBody = ({
  pointsTitle,
  pointsTitleClassName = 'font-body-medium-regular font-color-default',
  points,
  securityNoteKey,
  securityLink,
  selectedSegmentation,
  transactionType,
  realEstateCategory,
}: SegmentationBoxBodyProps) => {
  const { t } = useTranslation()

  const handleFindOutMoreClick = () => {
    trackSegmentationEvent({
      eventName: 'find_out_more_click',
      selectedSegmentation,
      transactionType,
      realEstateCategory,
    })
  }

  return (
    <div className={`${styles.footer} gap-l padding-l grid grid-flex`}>
      <div className={'grid-item one-whole grid grid-flex gap-s'}>
        {pointsTitle && <div className={`grid-item one-whole ${pointsTitleClassName}`}>{t(pointsTitle)}</div>}
        {points.map((p, index) => {
          const text1 = p.text1Key ? (p.text1Key.includes('.') ? t(p.text1Key) : p.text1Key) : undefined
          const text2 = p.text2Key.includes('.') ? t(p.text2Key) : p.text2Key
          return <SegmentationPoint key={index} text1={text1} text2={text2} noBullet={p.noBullet} />
        })}
      </div>
      {securityLink ? (
        <div className={`grid-item one-whole`}>
          <SecurityLink href={securityLink.href} label={t(securityLink.labelKey)} onClick={handleFindOutMoreClick} />
        </div>
      ) : (
        securityNoteKey && (
          <div className={`grid-item one-whole`}>
            <SecurityNote text={t(securityNoteKey)} />
          </div>
        )
      )}
    </div>
  )
}
